import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Grid } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { post } from 'api';
import { formatPrice } from 'lib/utils';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import IconifyIcon from 'components/base/IconifyIcon';
import StyledTextField from 'components/styled/StyledTextField';

const PayrollTable = () => {
  const [selectedMonth, setSelectedMonth] = useState(moment().subtract(1, 'month'));
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
    search: '',
  });
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({
    grossPayable: 0,
    netPayable: 0,
    earnings: {
      basic: 0,
      hra: 0,
      specialAllowance: 0,
    },
    deductions: {
      professionalTax: 0,
      pfEmployee: 0,
      pfEmployer: 0,
      esicEmployee: 0,
      esicEmployer: 0,
    },
  });
  const [openPayrollModal, setOpenPayrollModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [payrollMonth] = useState(moment().subtract(1, 'month'));

  const columns = [
    {
      field: 'userName',
      headerName: 'User',
      sortable: false,
      filterable: false,
      minWidth: 160,
      headerClassName: 'sticky-left-header',
      cellClassName: 'sticky-left',
      renderCell: (params) => {
        const firstName = params.row?.userId?.firstName;
        const lastName = params.row?.userId?.lastName;
        const empId = params.row?.userId?.empId;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {`${firstName} ${lastName}`}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {empId}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'monthlyCTC',
      headerName: 'CTC',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const monthlyCTC = params.row?.salarySnapshot?.monthlyCTC;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {monthlyCTC}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'Days',
      headerName: 'Days',
      sortable: false,
      filterable: false,
      minWidth: 50,
      renderCell: (params) => {
        const totalWorkingDays = params.row?.attendanceSnapshot?.totalWorkingDays;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {totalWorkingDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'payableDays',
      headerName: 'Payable Days',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const payableDays = params.row?.attendanceSnapshot?.payableDays;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400, textWrap: 'wrap' }}>
              {payableDays}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'grossPayable',
      headerName: 'Gross',
      sortable: false,
      filterable: false,
      minWidth: 100,
      renderCell: (params) => {
        const grossPayable = params.row?.grossPayable;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {grossPayable}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'basic',
      headerName: 'Basic',
      sortable: false,
      filterable: false,
      minWidth: 100,
      renderCell: (params) => {
        const basic = params.row?.earnings?.basic;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {basic}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'hra',
      headerName: 'HRA',
      sortable: false,
      filterable: false,
      minWidth: 100,
      renderCell: (params) => {
        const hra = params.row?.earnings?.hra;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {hra}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'specialAllowance',
      headerName: 'Allowance',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const specialAllowance = params.row?.earnings?.specialAllowance;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {specialAllowance}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'professionalTax',
      headerName: 'TAX',
      sortable: false,
      filterable: false,
      minWidth: 60,
      renderCell: (params) => {
        const professionalTax = params.row?.deductions?.professionalTax;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {professionalTax}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'pfEmployer',
      headerName: 'PF Employer',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const pfEmployer = params.row?.deductions?.pfEmployer;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {pfEmployer}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'pfEmployee',
      headerName: 'PF Employee',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const pfEmployee = params.row?.deductions?.pfEmployee;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {pfEmployee}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'esicEmployer',
      headerName: 'ESIC Employer',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const esicEmployer = params.row?.deductions?.esicEmployer;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {esicEmployer}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'esicEmployee',
      headerName: 'ESIC Employee',
      sortable: false,
      filterable: false,
      minWidth: 120,
      renderCell: (params) => {
        const esicEmployee = params.row?.deductions?.esicEmployee;
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {esicEmployee}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'netPayable',
      headerName: 'Net Payable',
      sortable: false,
      filterable: false,
      minWidth: 120,
      headerClassName: 'sticky-right-header',
      cellClassName: 'sticky-right',
      renderCell: (params) => {
        return (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
              {params.row?.netPayable}
            </Typography>
          </Box>
        );
      },
    },
  ];

  const fetchData = async () => {
    try {
      const month = selectedMonth.format('M');
      const year = selectedMonth.format('YYYY');
      const res = await post('/admin/getMonthlyPayrollList', {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        search: search,
        month,
        year,
      });
      if (res.status == 200) {
        setRows(res.data.data);
        setTotal(res.data.total);
        setSummary({
          grossPayable: 407678,
          netPayable: 386116,
          earnings: {
            basic: 203839,
            hra: 81536,
            specialAllowance: 122303,
          },
          deductions: {
            professionalTax: 1600,
            pfEmployee: 9532,
            pfEmployer: 10432,
            esicEmployee: 0,
            esicEmployer: 0,
          },
        });
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error' });
        setRows([]);
        setTotal(0);
        setSummary({
          grossPayable: 0,
          netPayable: 0,
          earnings: {
            basic: 0,
            hra: 0,
            specialAllowance: 0,
          },
          deductions: {
            professionalTax: 0,
            pfEmployee: 0,
            pfEmployer: 0,
            esicEmployee: 0,
            esicEmployer: 0,
          },
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedMonth, paginationModel, search]);

  const handleRunPayroll = async () => {
    try {
      setLoading(true);
      const month = payrollMonth.format('M');
      const year = payrollMonth.format('YYYY');
      const res = await post('/admin/run', {
        month,
        year,
        password,
      });
      if (res.status == 200) {
        enqueueSnackbar('Payroll generated successfully', { variant: 'success' });
        fetchData();
      } else {
        enqueueSnackbar(res.message || 'Error', { variant: 'error' });
      }

      setOpenPayrollModal(false);
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  const SummaryCard = ({ icon, amount, text }) => {
    console.log(icon);
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '20px',
          backgroundColor: '#F7FAFC',
          position: 'relative',
          minHeight: 40,
        }}
      >
        <CardContent>
          {/* Icon Circle */}
          {/* <Box
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              backgroundColor: '#EAF3FD',
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box> */}

          <Typography variant="subtitle1" color="text.secondary">
            {text}
          </Typography>

          <Typography variant="h6" fontWeight={600} mt={2}>
            {formatPrice(amount, 0, true)}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <Paper sx={{ flex: 1, p: { xs: 3, md: 3 } }}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <StyledTextField
            id="search-box"
            type="search"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search User Name"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconifyIcon icon="material-symbols:search-rounded" fontSize={20} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              maxWidth: { sm: 200, md: 240 },
              flexGrow: { xs: 1, sm: 0 },
            }}
          />
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
          >
            <Button
              variant="soft"
              shape="square"
              color="neutral"
              sx={{ gap: 0.5, p: 2 }}
              onClick={() => setOpenPayrollModal(true)}
            >
              <IconifyIcon icon="material-symbols:add-2-rounded" />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'block' } }}>
                Run Payroll
              </Box>
            </Button>
            <DatePicker
              views={['month', 'year']}
              label="Month"
              value={selectedMonth}
              maxDate={moment().subtract(1, 'month')}
              onChange={(newValue) => {
                setSelectedMonth(newValue);
                console.log(newValue.format('MM-YYYY'));
              }}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 180 },
                },
              }}
            />
          </Box>
        </Box>
      </LocalizationProvider>
      <Grid container spacing={3} mb={2}>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard icon={'k'} amount={summary.grossPayable} text={'Gross'} />
        </Grid>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard icon={'k'} amount={summary.netPayable} text={'Net'} />
        </Grid>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard icon={'k'} amount={summary.earnings.basic} text={'Basic'} />
        </Grid>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard icon={'k'} amount={summary.earnings.hra} text={'HRA'} />
        </Grid>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard icon={'k'} amount={summary.earnings.specialAllowance} text={'Allowances'} />
        </Grid>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard
            icon={'k'}
            amount={summary.deductions.esicEmployee + summary.deductions.esicEmployer}
            text={'ESIC'}
          />
        </Grid>
        <Grid item size={{ xs: 12, sm: 1.7, md: 1.7, lg: 1.7 }}>
          <SummaryCard
            icon={'k'}
            amount={summary.deductions.pfEmployee + summary.deductions.pfEmployer}
            text={'PF'}
          />
        </Grid>
      </Grid>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={false}
          autoHeight
          paginationModel={paginationModel}
          onPaginationModelChange={(e) => {
            setPaginationModel(() => ({
              page: e.page,
              pageSize: e.pageSize,
              search: search,
            }));
          }}
          rowCount={total}
          pageSizeOptions={[5, 10, 20]}
        />
      </Box>

      <Dialog
        open={openPayrollModal}
        onClose={() => !loading && setOpenPayrollModal(false)}
        disableEscapeKeyDown={loading}
      >
        <DialogTitle>Run Payroll</DialogTitle>

        <DialogContent sx={{ width: 450, pt: 2 }}>
          {!loading && (
            <>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to run payroll for
                <b>
                  {' '}
                  {payrollMonth.format('MMM')}-{payrollMonth.format('YYYY')}
                </b>{' '}
                ?
              </Typography>
              <Typography sx={{ mb: 2 }}>
                Please review the attendance summary carefully. Once the payroll is processed, it
                cannot be revised.
              </Typography>

              {/* <DatePicker
                views={['month', 'year']}
                label="Month"
                value={selectedMonth}
                maxDate={moment().subtract(1, 'month')}
                onChange={(newValue) => {
                  setSelectedMonth(newValue);
                  console.log(newValue.format('MM-YYYY'));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 180, mb: 2 },
                  },
                }}
              /> */}

              <TextField
                label="Password"
                type="password"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </>
          )}

          {loading && (
            <Box
              sx={{
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography>Running payroll, please wait...</Typography>
            </Box>
          )}
        </DialogContent>

        {!loading && (
          <DialogActions>
            <Button onClick={() => setOpenPayrollModal(false)}>Cancel</Button>

            <Button variant="contained" onClick={handleRunPayroll} disabled={!password}>
              Submit
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Paper>
  );
};

export default PayrollTable;
